<template>
    <div
        ref="wrapper"
        :class="{ [`l-ss-c-offers-wrapper-${view}`]: true }"
        class="l-ss-c-offers-wrapper"
        @contextmenu="globalHandleMouseDown"
        @mousedown="globalHandleMouseDown"
        @click="globalHandleOfferClick"
        @touchstart="onTouchStart($event)"
        @touchend="onTouchEnd($event)"
        @touchmove="onTouchEnd($event)">
        <div
            ref="items"
            :class="classWrapper"
            @scroll="handleScroll">
            <template v-for="offer in offers">
                <offer
                    :key="`offer-${offer.id}`"
                    :is-cart-available="$configuration.features.addToCartButton"
                    :offer="offer"
                    :style="offerStyle"
                    :templates="templates"
                    :class="classOffersItem"
                    ref="offer"
                    v-bind="offerProps"
                    @add-to-cart="addToCard"/>
            </template>
            <slot name="append"></slot>
        </div>
        <template v-if="showArrows">
            <button
                ref="carousel-left"
                v-show="showArrowsLeft"
                class="l-ss-c-results-carousel-button l-ss-c-results-carousel-button-prev"
                @click="slide('left')">
                <i
                    class="l-ss-c-icon"
                    v-html="iconArrowRight"></i>
            </button>
            <button
                ref="carousel-right"
                v-show="showArrowsRight"
                class="l-ss-c-results-carousel-button l-ss-c-results-carousel-button-next"
                @click="slide('right')">
                <i
                    class="l-ss-c-icon"
                    v-html="iconArrowRight"></i>
            </button>
        </template>
    </div>
</template>

<script>
    import Offer from './offer/offer';
    import IconArrowRight from '../assets/images/icons/arrow-right-results.svg';
    import { AVAILABLE_VIEWS } from '../../init/options/OffersOptions';
    import debounce from '../../core/helpers/debounce';

    let SLIDE_SIZE = 450;
    const SLIDE_DELAY = 75;
    const DEFAULT_GRID_COLUMNS = 4;
    const LONG_TOUCH_DURATION = 500;

    export default {
        components: {
            Offer,
        },
        props: {
            offers: {
                type: Array,
                default: () => [],
            },
            type: {
                type: String,
                default: 'offers',
            },
            templates: {
                type: Object,
                required: false,
            },
            view: {
                type: String,
                default: 'row',
                validate: (value) => AVAILABLE_VIEWS.includes(value),
            },
            offerProps: {
                type: Object,
                default: () => ({}),
            },
            columns: {
                type: Number,
                default: DEFAULT_GRID_COLUMNS,
            },
        },
        data() {
            return {
                showArrows: false,
                showArrowsLeft: true,
                showArrowsRight: true,
                mouseDownInfo: null,
                scrollLeft: 0,
                keepSlideButtonVisible: true,
                handleScroll: () => {},
                longTouchTimer: null,
                isAddToCartTriggered: false,
                viewedOffers: [],
            };
        },
        computed: {
            isRowsTemplate() {
                return this.$configuration.layout.templateOffer === 'rows';
            },
            classWrapper() {
                return this.isRowsTemplate ? 'l-ss-c-offers-wrapper-items-rows' :
                    'l-ss-c-offers-wrapper-items';
            },
            classOffersItem() {
                return this.isRowsTemplate ? 'l-ss-c-offers-item-rows' :
                    'l-ss-c-offers-item';
            },
            iconArrowRight() {
                return this.$configuration.customSvgIconRightArrow ? this.$configuration.customSvgIconRightArrow : IconArrowRight;
            },
            offerStyle() {
                const style = {};
                if (this.view === 'grid') {
                    style['--offer-item-width'] = `${100 / this.columns}%`;
                }
                return style;
            },
        },
        watch: {
            offers: {
                immediate: true,
                async handler() {
                    await this.$nextTick();
                    this.initOfferVisibilityObserver();
                    this.handleArrows();
                },
            },
        },
        mounted() {
            this.$nextTick(() => {
                this.scrollToOffer();
            });
            
            this.handleArrows();
            this.handleScroll = debounce(this.handleScrollUser.bind(this), SLIDE_DELAY);
        },
        methods: {
            initOfferVisibilityObserver() {
                const observer = new IntersectionObserver((entries) => {
                    this.viewedOffers = JSON.parse(sessionStorage.getItem('viewedOffers')) || [];

                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            const offerId = entry.target.dataset.offerId;

                            if (!this.viewedOffers.includes(offerId)) {
                                this.viewedOffers.push(offerId);
                                sessionStorage.setItem('viewedOffers', JSON.stringify(this.viewedOffers));
                            }

                            observer.unobserve(entry.target);
                        }
                    });
                }, {
                    rootMargin: '0px',
                    threshold: 1.0,
                });

                const offerElements = this.$refs.items?.children;

                for (const offer of offerElements) {
                    observer.observe(offer);
                }
            },
            scrollToOffer() {
                const hash = window.location.hash;
                if (hash.includes('idOffer=')) {
                    const params = new URLSearchParams(hash.substring(1));
                    const id = params.get('idOffer');
                    const popupElement = document.querySelector('searchbooster-search-popup');
                    if (popupElement && popupElement.shadowRoot) {
                        const element = popupElement.shadowRoot.querySelector(`[data-offer-id="${id}"]`);
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }
                }
            },
            onTouchStart(event) {
                const customComposedPath = event.composedPath && event.composedPath();
                this.globalHandleMouseDown(event);
                this.longTouchTimer = setTimeout(() => this.onLongTouch(event, customComposedPath), LONG_TOUCH_DURATION);
            },
            onTouchEnd(event) {
                if (this.longTouchTimer) {
                    clearTimeout(this.longTouchTimer);
                }
            },
            onLongTouch(event, customComposedPath) {
                this.globalHandleOfferClick(event, {}, customComposedPath);
            },
            slide(direction = 'right') {
                this.keepSlideButtonVisible = true;
                const itemsWrapper = this.$refs['items'];
                const offer = this.$refs['offer'];
                if (offer) {
                    SLIDE_SIZE = this.$screen.isMobile ? offer[0].$el?.offsetWidth :
                        offer[0].$el?.offsetWidth * this.$configuration.limitCaruselOffers;
                }
                const maxScrollLeft = itemsWrapper.scrollWidth - itemsWrapper.clientWidth;
                const scrollLeft = direction === 'right' ?
                    itemsWrapper.scrollLeft + SLIDE_SIZE :
                    itemsWrapper.scrollLeft - SLIDE_SIZE;
                itemsWrapper.scroll({
                    left: scrollLeft,
                    behavior: 'smooth',
                });
                if (direction === 'right') {
                    this.showArrowsLeft = true;
                    this.showArrowsRight = scrollLeft < maxScrollLeft;
                } else {
                    this.showArrowsLeft = scrollLeft > 0;
                    this.showArrowsRight = true;
                }
                this.scrollLeft = scrollLeft;
            },
            getOfferPosition(index, options = {}) {
                const OFFERS_PER_ROW_MOBILE = 2;
                const OFFERS_PER_ROW_CUT = this.columns - 1;
                const OFFERS_PER_ROW_DEFAULT = options.groupIndex !== undefined ? OFFERS_PER_ROW_CUT : this.columns;
                const ADAPTIVE_OFFERS_PER_ROW = this.$screen.isMobile ? OFFERS_PER_ROW_MOBILE : OFFERS_PER_ROW_DEFAULT;
                let pos = index + 1;
                let row = Math.floor(index / ADAPTIVE_OFFERS_PER_ROW) + 1;
                let page = isNaN(parseInt(options.page)) ? 1 : parseInt(options.page) || 1;
                return {
                    pos: (options.startIndex ? index + options.startIndex + 1 : pos) * page,
                    row: options.row ? parseInt(options.row) : row,
                    col: this.view === 'row' ? index + 1 : ADAPTIVE_OFFERS_PER_ROW -
                        ((row * ADAPTIVE_OFFERS_PER_ROW) - pos) +
                        (ADAPTIVE_OFFERS_PER_ROW < OFFERS_PER_ROW_DEFAULT ? (options.groupIndex || 0) : 0),
                };
            },
            handleScrollUser() {
                if (this.keepSlideButtonVisible) {
                    this.keepSlideButtonVisible = false;
                    return;
                }
                const itemsWrapper = this.$refs['items'];
                
                if (this.scrollLeft > itemsWrapper.scrollLeft) {
                    this.showArrowsLeft = false;
                    this.showArrowsRight = true;
                } else if (this.scrollLeft < itemsWrapper.scrollLeft) {
                    this.showArrowsLeft = true;
                    this.showArrowsRight = false;
                }
                this.scrollLeft = itemsWrapper.scrollLeft;
            },
            addToCard(offer) {
                this.isAddToCartTriggered = true;
                this.$emit('add-to-cart', offer);
            },
            globalHandleOfferClick(event, positionOptions = {}, customComposedPath = null) {
                let eventName = 'offer-click';
                let source = 'productClick';
                if (this.isAddToCartTriggered) {
                    eventName = 'add-to-cart-click';
                    source = 'addToCart';
                }
                let path = event.path || (event.composedPath && event.composedPath());
                if (customComposedPath) {
                    path = customComposedPath;
                }
                const offerTarget = path.find(element => element.dataset && element.dataset.offerId);

                if (offerTarget && this.mouseDownInfo) {
                    const sameLocation =
                        this.mouseDownInfo.pageX === event.pageX &&
                        this.mouseDownInfo.pageY === event.pageY;
                    if (Boolean(this.mouseDownInfo) === false || sameLocation === false) {
                        return;
                    }
                    const offerId = offerTarget.dataset.offerId;
                    const offer = this.offers.find(item => item.id === offerId);
                    const positionIndex = Array.prototype.indexOf.call(offerTarget.parentNode.children, offerTarget);
                    this.$emit(eventName, Object.assign({
                        event: event,
                        source: source,
                        id: offer.id,
                        url: offer.url,
                        customEventType: customComposedPath ? 'contextmenu' : null,
                    }, this.getOfferPosition(positionIndex, Object.assign(positionOptions, {
                        groupIndex: offerTarget.dataset.groupIndex,
                        offersInRow: offerTarget.dataset.offersInRow,
                        page: offerTarget.dataset.page,
                        row: offerTarget.dataset.row,
                    }))));
                }
                this.mouseDownInfo = null;
                this.isAddToCartTriggered = false;
            },
            globalHandleMouseDown(event) {
                this.mouseDownInfo = {
                    pageX: event.pageX,
                    pageY: event.pageY,
                };
            },
            handleArrows() {
                this.$nextTick(() => {
                    if (this.view === 'row' && this.$refs.wrapper && this.$refs.items && this.$configuration.layout.templateOffer !== 'rows') {
                        this.showArrows = this.$refs.items.scrollWidth > this.$refs.wrapper.offsetWidth;
                        this.showArrowsLeft = false;
                    } else {
                        this.showArrows = false;
                    }
                });
            },
        },
    };
</script>
